<script lang="ts">
    import ColorPicker from './ColorPicker.svelte'
    import { createForm } from 'svelte-forms-lib'
    import type { IChatSetting } from '../lib/types'
    import { onMount } from 'svelte'
    import FontHelper from '../helpers/FontHelper'

    export let settings: IChatSetting
    export let save

    const defaultSettings: IChatSetting = {
        color: '#000',
        family: 'Roboto',
        files: {},
        styles: [],
        fontSize: 18,
        messagesPerMinute: 0,
        upperCase: false,
        fontWeight: 400,
        fontStyle: 'regular',
        underline: false,
    }

    const fonts = ['Calibri', 'Roboto', 'San Serif']
    const colors = ['#fff', '#000']

    let currentFonts = []
    let selectedFont = null
    let fontSelectOpen = false
    let fontSelect

    // Google fonts merges weights and styles as "variants" when we select a font family we
    // need to

    let styles = {}

    let fontWeights = [400]
    let fontStyles = ['regular']

    let timer = null
    const MINIMUM_SCROLL_DEBOUNCE_TIME = 500
    const MAX_FONTS_TO_LOAD = 10

    onMount(async () => {
        await getFonts()
        await loadInitialFonts()
        fontSelect.addEventListener('scroll', function (event) {
            // Every time the user scrolls save the current time
            // if
            if (!timer !== null) {
                clearTimeout(timer)
            }

            timer = setTimeout(() => {
                loadDisplayedFonts(event)
            }, MINIMUM_SCROLL_DEBOUNCE_TIME)
        })
    })

    async function loadInitialFonts() {
        await FontHelper.loadFontsBetweenIndexesOf(0, MAX_FONTS_TO_LOAD)
    }

    async function loadDisplayedFonts(event) {
        // Get the dimensions of the font display + an individual font-item
        const { offsetHeight, scrollHeight, scrollTop } = event.target
        if (scrollTop >= scrollHeight - offsetHeight) {
            // getFonts(currentFonts.length)
        }
        // should have a collection of option elements, pick the first and get the dimensions
        const [option, ...rest] = document.querySelectorAll('.font-option')

        // Every time a new font is loaded the div can change size
        // calculate the average height of all divs every time scroll completes
        const TOTAL_FONTS = currentFonts.length
        const averageElementHeight =
            Math.round(scrollHeight + offsetHeight) / TOTAL_FONTS

        // Divide scrollTop (distance scrolled) by averageElmentHeight to reach index of element being shown
        let startFontIndex = Math.round(scrollTop / averageElementHeight)
        let indexOffSet

        if (startFontIndex < 10) {
            indexOffSet = 0
        } else if (startFontIndex > 10 && startFontIndex < 50) {
            indexOffSet = startFontIndex / 10
        } else if (startFontIndex > 50) {
            indexOffSet = 5
        }

        let endFontIndex = startFontIndex + MAX_FONTS_TO_LOAD
        await FontHelper.loadFontsBetweenIndexesOf(
            startFontIndex - indexOffSet,
            endFontIndex + indexOffSet
        )
    }

    const { form, handleChange, handleSubmit, touched } = createForm({
        initialValues: settings || defaultSettings,
        onSubmit: (values) => {
            return save(values)
        },
    })

    function updateColor(hexColor) {
        $form.color = hexColor
    }

    function toggleUpperCase() {
        $form.upperCase = $form.upperCase ? false : true
    }

    function toggleUnderline() {
        $form.underline = $form.underline ? false : true
    }

    async function getFonts() {
        const res = await FontHelper.fonts
        if (!res) return false
        currentFonts = [...currentFonts, ...res]
        const currentStyles = {}
        currentFonts.forEach((font) => {
            const properties = {}

            if (!properties['regular']) {
                properties['regular'] = []
            }
            if (!font) return false
            font.styles.forEach((style) => {
                if (style.length === 3) {
                    properties['regular'].push(style)
                    return true
                }

                if (style === 'italic') {
                    if (!properties[style]) {
                        properties[style] = []
                    }
                    properties[style] = [...properties[style], '200']
                    return true
                } else if (style === 'regular') {
                    if (!properties[style]) {
                        properties[style] = []
                    }
                    properties[style] = [...properties[style], '400']
                    return true
                } else if (style === 'bold') {
                    if (!properties[style]) {
                        properties[style] = []
                    }
                    properties[style] = [...properties[style], '600']
                    return true
                }

                const value = style.substring(0, 3)
                const key = style.substring(3, style.length)
                if (!properties[key]) {
                    properties[key] = []
                }
                properties[key] = [...properties[key], value]
            })
            currentStyles[font.family] = properties
        })
        styles = currentStyles
    }

    function handleSelect(event) {
        const family = $form.family
        const fontObject = currentFonts.find((font) => {
            return font.family === family
        })
        $form.files = fontObject.files
        $form.styles = fontObject.styles
    }
</script>

<form
    on:submit={handleSubmit}
    class="flex flex-1 flex-col border-b border-gray-200">
    <div class="px-4 py-5 sm:p-6">
        <div class="grid grid-cols-3 gap-4 w-full">
            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    for="messagesPerMinute"
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700">
                    Messages Per Minute
                </label>
                <div class="flex rounded-md shadow-sm">
                    <input
                        id="messagesPerMinute"
                        name="messagesPerMinute"
                        type="number"
                        bind:value={$form.messagesPerMinute}
                        class="form-input flex-1 block w-full rounded-none rounded-r-md transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        placeholder="5" />
                </div>
            </div>

            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700"
                    for="font">Font</label>

                <div class="relative">
                    <span
                        on:click={() => (fontSelectOpen = !fontSelectOpen)}
                        class="inline-block w-full rounded-md shadow-sm">
                        <button
                            type="button"
                            aria-haspopup="listbox"
                            aria-expanded="true"
                            aria-labelledby="listbox-label"
                            class="cursor-default relative w-full rounded-md border border-gray-300 bg-white pl-3 pr-10 py-2 text-left focus:outline-none focus:shadow-outline-orange focus:border-orange-300 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                            <span
                                class="block truncate"
                                style={`font-family: ${$form.family}`}>
                                {$form.family}
                            </span>
                            <span
                                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                <svg
                                    class="h-5 w-5 text-gray-400"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    stroke="currentColor">
                                    <path
                                        d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round" />
                                </svg>
                            </span>
                        </button>
                    </span>

                    <div
                        class:hidden={!fontSelectOpen}
                        class="z-50 absolute mt-1 w-full rounded-md bg-white shadow-lg">
                        <ul
                            bind:this={fontSelect}
                            id="font"
                            tabindex="-1"
                            role="listbox"
                            aria-labelledby="listbox-label"
                            aria-activedescendant="listbox-item-3"
                            class=" max-h-60 rounded-md py-1 text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5">
                            {#each currentFonts as font}
                                <li
                                    on:click={(event) => {
                                        fontSelectOpen = !fontSelectOpen
                                        $form.family = font.family
                                        handleChange(event)
                                    }}
                                    id="listbox-option-0"
                                    role="option"
                                    class="font-option text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9">
                                    <span
                                        class="font-normal block truncate"
                                        style={`font-family: ${font.family}`}>
                                        {font.family}
                                    </span>
                                </li>
                            {/each}
                        </ul>
                    </div>
                </div>
            </div>

            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700"
                    for="fontWeight">Font Style</label>
                <select
                    class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-orange focus:border-orange-300 sm:text-sm sm:leading-5"
                    id="fontWeight"
                    name="fontWeight"
                    on:blur={handleChange}
                    bind:value={$form.fontStyle}>
                    {#if $form.family && currentFonts.length}
                        {#each Object.keys(styles[$form.family]) as style}
                            <option>{style}</option>
                        {/each}
                    {/if}
                </select>
            </div>

            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700"
                    for="fontWeight">Font Weight</label>
                <select
                    class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-orange focus:border-orange-300 sm:text-sm sm:leading-5"
                    id="fontWeight"
                    name="fontWeight"
                    on:blur={handleChange}
                    bind:value={$form.fontWeight}>
                    {#if $form.family && currentFonts.length}
                        {#each styles[$form.family][$form.fontStyle] as weight}
                            <option>{weight}</option>
                        {/each}
                    {/if}
                </select>
            </div>

            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700"
                    for="font">Text Uppercase</label>
                <span
                    on:click={toggleUpperCase}
                    role="checkbox"
                    tabindex="0"
                    aria-checked="true"
                    class:background={$form.upperCase}
                    class="background-default relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-gray-200 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:shadow-outline">
                    <span
                        aria-hidden="true"
                        class:inner-toggle-button={$form.upperCase}
                        class="translate-x-0 relative inline-block h-5 w-5 rounded-full bg-white shadow transform transition ease-in-out duration-200">
                        <span
                            class:toggle-inner={$form.upperCase}
                            class="opacity-100 ease-in duration-200 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity">
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12">
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </span>
                        <span
                            class:toggle-outer={$form.upperCase}
                            class="opacity-0 ease-out duration-100 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity">
                            <svg
                                class="h-3 w-3 text-orange-600"
                                fill="currentColor"
                                viewBox="0 0 12 12">
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                            </svg>
                        </span>
                    </span>
                </span>
            </div>

            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700"
                    for="color">Color</label>
                <div class="mt-2 ">
                    <ColorPicker color={$form.color} {updateColor} />
                </div>
            </div>

            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700"
                    for="font">Underline</label>
                <span
                    on:click={toggleUnderline}
                    role="checkbox"
                    tabindex="0"
                    aria-checked="true"
                    class:background={$form.underline}
                    class="background-default relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-gray-200 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:shadow-outline">
                    <span
                        aria-hidden="true"
                        class:inner-toggle-button={$form.underline}
                        class="translate-x-0 relative inline-block h-5 w-5 rounded-full bg-white shadow transform transition ease-in-out duration-200">
                        <span
                            class:toggle-inner={$form.underline}
                            class="opacity-100 ease-in duration-200 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity">
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12">
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </span>
                        <span
                            class:toggle-outer={$form.underline}
                            class="opacity-0 ease-out duration-100 absolute inset-0 h-full w-full flex items-center justify-center transition-opacity">
                            <svg
                                class="h-3 w-3 text-orange-600"
                                fill="currentColor"
                                viewBox="0 0 12 12">
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                            </svg>
                        </span>
                    </span>
                </span>
            </div>

            <div class="col-span-3 grid-cols-3 grid items-center">
                <label
                    class="block col-span-2 text-sm font-medium leading-5 text-gray-700"
                    for="fontSize">Font Size</label>
                <div class="mt-1 py-2 relative rounded-md">
                    <input
                        id="fontSize"
                        name="fontSize"
                        type="number"
                        min={8}
                        max={72}
                        on:blur={handleChange}
                        bind:value={$form.fontSize}
                        class="form-input block w-full sm:text-sm sm:leading-5" />
                    <p class="text-xs">Size in px</p>
                </div>
            </div>
        </div>
    </div>
    <div class="px-4 pb-3 text-right sm:px-6">
        <span class="inline-flex rounded-md shadow-sm">
            <button
                type="submit"
                class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-500 focus:outline-none focus:border-orange-700 focus:shadow-outline-orange active:bg-orange-700 transition duration-150 ease-in-out">
                Save
            </button>
        </span>
    </div>
</form>

<style>
    .translate {
        @apply translate-x-5;
    }

    .background-default {
        @apply bg-gray-200;
    }

    .background {
        @apply bg-orange-600;
    }

    .inner-toggle-button {
        @apply translate-x-5;
    }

    .toggle-inner {
        @apply opacity-0 ease-out duration-100;
    }

    .toggle-outer {
        @apply opacity-100 ease-in duration-200;
    }
</style>
